/**
 * Empty-note detection — the single source of truth for "the user did nothing to this note".
 *
 * A note is considered EMPTY (safe to auto-delete when the user leaves it) only when ALL hold:
 *   - it is not already trashed (deleted / deleted_at) or archived (archived / archived_at)
 *   - it is not pinned
 *   - it is not PIN-protected
 *   - it is not shared (no live row in promptnote_shared_notes — passed in via options.isShared)
 *   - it is a regular note (note_type 'note'/unset), not a skill note
 *   - it has no tags at all (auto-tag vs manual tag cannot be told apart, so keep on any tag)
 *   - it has no snippet rows for it at all (soft-deleted snippets still count as "was used")
 *   - its title is a default value: '', 'Untitled Note', or the auto timestamp
 *     `Note - <MMM d, h:mm a>` written by MainEditor.handleTitleBlur
 *
 * Everything else is treated as "the user did something" and must be kept.
 */

export const DEFAULT_NOTE_TITLE = 'Untitled Note';

const AUTO_TIMESTAMP_TITLE_RE = /^Note - [A-Z][a-z]{2} \d{1,2}, \d{1,2}:\d{2} (AM|PM)$/;

export const isDefaultNoteTitle = (title) => {
  if (title == null) return true;
  const trimmed = String(title).trim();
  if (trimmed === '') return true;
  if (trimmed === DEFAULT_NOTE_TITLE) return true;
  return AUTO_TIMESTAMP_TITLE_RE.test(trimmed);
};

/**
 * @param {object|null} note - note row (as stored in notes$)
 * @param {Array<object>} snippets - all snippet rows (deleted included)
 * @param {{ isShared?: boolean }} [options]
 * @returns {boolean} true when the note is empty and may be auto-deleted
 */
export const isEmptyNote = (note, snippets = [], options = {}) => {
  if (!note || !note.id) return false;

  if (note.deleted || note.deleted_at) return false;
  if (note.archived || note.archived_at) return false;
  if (note.pinned || note.pinned_at) return false;
  if (note.is_protected === true) return false;
  if (note.is_shared === true || options.isShared === true) return false;

  if (note.note_type && note.note_type !== 'note') return false;

  if (Array.isArray(note.tags) && note.tags.length > 0) return false;

  const hasAnySnippet = Array.isArray(snippets)
    && snippets.some(snippet => snippet && snippet.note_id === note.id);
  if (hasAnySnippet) return false;

  return isDefaultNoteTitle(note.title);
};
